import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './css/ThemBones.module.css';

const DEFAULT_PARTS = [
  { id: 'torso', name: 'Torso', parentId: '', pivotX: 0.5, pivotY: 0.2, attachX: 0.5, attachY: 0.5, z: 20, rect: null },
  { id: 'head', name: 'Head', parentId: 'torso', pivotX: 0.5, pivotY: 0.85, attachX: 0.5, attachY: 0.05, z: 30, rect: null },
  { id: 'upper-arm-l', name: 'UpperArm_L', parentId: 'torso', pivotX: 0.5, pivotY: 0.1, attachX: 0.2, attachY: 0.25, z: 24, rect: null },
  { id: 'lower-arm-l', name: 'LowerArm_L', parentId: 'upper-arm-l', pivotX: 0.5, pivotY: 0.08, attachX: 0.5, attachY: 0.95, z: 22, rect: null },
  { id: 'upper-arm-r', name: 'UpperArm_R', parentId: 'torso', pivotX: 0.5, pivotY: 0.1, attachX: 0.8, attachY: 0.25, z: 18, rect: null },
  { id: 'lower-arm-r', name: 'LowerArm_R', parentId: 'upper-arm-r', pivotX: 0.5, pivotY: 0.08, attachX: 0.5, attachY: 0.95, z: 16, rect: null },
  { id: 'thigh-l', name: 'Thigh_L', parentId: 'torso', pivotX: 0.5, pivotY: 0.1, attachX: 0.36, attachY: 0.95, z: 26, rect: null },
  { id: 'calf-l', name: 'Calf_L', parentId: 'thigh-l', pivotX: 0.5, pivotY: 0.08, attachX: 0.5, attachY: 0.96, z: 25, rect: null },
  { id: 'thigh-r', name: 'Thigh_R', parentId: 'torso', pivotX: 0.5, pivotY: 0.1, attachX: 0.64, attachY: 0.95, z: 14, rect: null },
  { id: 'calf-r', name: 'Calf_R', parentId: 'thigh-r', pivotX: 0.5, pivotY: 0.08, attachX: 0.5, attachY: 0.96, z: 13, rect: null },
  { id: 'foot-l', name: 'Foot_L', parentId: 'calf-l', pivotX: 0.2, pivotY: 0.3, attachX: 0.52, attachY: 0.92, z: 25, rect: null },
  { id: 'foot-r', name: 'Foot_R', parentId: 'calf-r', pivotX: 0.2, pivotY: 0.3, attachX: 0.52, attachY: 0.92, z: 12, rect: null },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const degToRad = (degrees) => (degrees * Math.PI) / 180;

const rotate = (point, angle) => ({
  x: point.x * Math.cos(angle) - point.y * Math.sin(angle),
  y: point.x * Math.sin(angle) + point.y * Math.cos(angle),
});

const normalizeRect = (x1, y1, x2, y2, maxWidth, maxHeight) => {
  const minX = clamp(Math.min(x1, x2), 0, maxWidth);
  const minY = clamp(Math.min(y1, y2), 0, maxHeight);
  const maxX = clamp(Math.max(x1, x2), 0, maxWidth);
  const maxY = clamp(Math.max(y1, y2), 0, maxHeight);
  return {
    x: Math.round(minX),
    y: Math.round(minY),
    w: Math.round(maxX - minX),
    h: Math.round(maxY - minY),
  };
};

const drawChecker = (ctx, width, height, size = 8) => {
  for (let y = 0; y < height; y += size) {
    for (let x = 0; x < width; x += size) {
      const even = ((x / size) + (y / size)) % 2 === 0;
      ctx.fillStyle = even ? '#0b1220' : '#162036';
      ctx.fillRect(x, y, size, size);
    }
  }
};

const makeEmptyPoseFrame = (parts) =>
  parts.reduce((acc, part) => {
    acc[part.id] = { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
    return acc;
  }, {});

const syncPoseFrames = (frames, frameCount, parts) => {
  const nextFrames = [];
  const safeCount = clamp(frameCount, 1, 32);

  for (let i = 0; i < safeCount; i += 1) {
    const sourceFrame = frames[i] || {};
    const nextFrame = {};

    parts.forEach((part) => {
      const existing = sourceFrame[part.id];
      nextFrame[part.id] = existing
        ? {
            offsetX: Number(existing.offsetX) || 0,
            offsetY: Number(existing.offsetY) || 0,
            rotation: Number(existing.rotation) || 0,
            meshBend: Number(existing.meshBend) || 0,
            zOffset: Number(existing.zOffset) || 0,
          }
        : { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
    });

    nextFrames.push(nextFrame);
  }

  return nextFrames;
};

const ThemBones = () => {
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState('');
  const [parts, setParts] = useState(DEFAULT_PARTS);
  const [selectedPartId, setSelectedPartId] = useState(DEFAULT_PARTS[0].id);
  const [frameCount, setFrameCount] = useState(8);
  const [frameWidth, setFrameWidth] = useState(96);
  const [frameHeight, setFrameHeight] = useState(96);
  const [outputScale, setOutputScale] = useState(1);
  const [activeFrame, setActiveFrame] = useState(0);
  const [previewFrame, setPreviewFrame] = useState(0);
  const [playPreview, setPlayPreview] = useState(true);
  const [fps, setFps] = useState(10);
  const [error, setError] = useState('');
  const [poseFrames, setPoseFrames] = useState(() => syncPoseFrames([], 8, DEFAULT_PARTS));
  const [meshEnabled, setMeshEnabled] = useState(true);
  const [meshSegments, setMeshSegments] = useState(8);
  const [confirmState, setConfirmState] = useState({ open: false, message: '' });

  const editorCanvasRef = useRef(null);
  const poseCanvasRef = useRef(null);
  const stripCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const drawMetaRef = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const drawingRef = useRef(null);
  const dragPoseRef = useRef(null);
  const autoPartCounterRef = useRef(1);
  const confirmActionRef = useRef(null);

  const selectedPart = useMemo(
    () => parts.find((part) => part.id === selectedPartId) || null,
    [parts, selectedPartId]
  );

  const selectedPose = useMemo(() => {
    const frame = poseFrames[activeFrame] || {};
    return frame[selectedPartId] || { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
  }, [poseFrames, activeFrame, selectedPartId]);

  const openConfirm = (message, onConfirm) => {
    confirmActionRef.current = onConfirm;
    setConfirmState({ open: true, message });
  };

  const closeConfirm = () => {
    confirmActionRef.current = null;
    setConfirmState({ open: false, message: '' });
  };

  const acceptConfirm = () => {
    const action = confirmActionRef.current;
    closeConfirm();
    if (action) action();
  };

  useEffect(() => {
    setPoseFrames((current) => syncPoseFrames(current, frameCount, parts));
    setActiveFrame((current) => clamp(current, 0, Math.max(0, frameCount - 1)));
    setPreviewFrame((current) => clamp(current, 0, Math.max(0, frameCount - 1)));
  }, [frameCount, parts]);

  const updatePart = (partId, updates) => {
    setParts((currentParts) =>
      currentParts.map((part) => (part.id === partId ? { ...part, ...updates } : part))
    );
  };

  const updatePose = (frameIndex, partId, updates) => {
    setPoseFrames((current) => {
      const next = [...current];
      const frame = { ...(next[frameIndex] || {}) };
      const partPose = frame[partId] || { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
      frame[partId] = { ...partPose, ...updates };
      next[frameIndex] = frame;
      return next;
    });
  };

  const clearFramePose = (frameIndex) => {
    openConfirm(`Clear all poses in frame ${frameIndex + 1}? This cannot be undone.`, () => {
      setPoseFrames((current) => {
        const next = [...current];
        next[frameIndex] = makeEmptyPoseFrame(parts);
        return next;
      });
    });
  };

  const copyFromPreviousFrame = () => {
    if (activeFrame <= 0) return;

    setPoseFrames((current) => {
      const next = [...current];
      const previous = current[activeFrame - 1] || makeEmptyPoseFrame(parts);
      next[activeFrame] = JSON.parse(JSON.stringify(previous));
      return next;
    });
  };

  const addPart = () => {
    const nextIndex = autoPartCounterRef.current;
    autoPartCounterRef.current += 1;

    const newPart = {
      id: `part-${nextIndex}`,
      name: `Part_${nextIndex}`,
      parentId: '',
      pivotX: 0.5,
      pivotY: 0.5,
      attachX: 0.5,
      attachY: 0.5,
      z: 20,
      rect: null,
    };

    setParts((currentParts) => [...currentParts, newPart]);
    setSelectedPartId(newPart.id);
  };

  const removeSelectedPart = () => {
    if (!selectedPart || parts.length <= 1) return;
    openConfirm(`Remove bone part "${selectedPart.name}"?`, () => {
      const nextParts = parts.filter((part) => part.id !== selectedPart.id);
      const sanitized = nextParts.map((part) =>
        part.parentId === selectedPart.id ? { ...part, parentId: '' } : part
      );

      setParts(sanitized);
      setSelectedPartId(sanitized[0].id);
    });
  };

  const resetRig = () => {
    openConfirm('Reset the rig and clear all current frame poses?', () => {
      setParts(DEFAULT_PARTS);
      setSelectedPartId(DEFAULT_PARTS[0].id);
      setPoseFrames(syncPoseFrames([], frameCount, DEFAULT_PARTS));
      setError('');
    });
  };

  const onImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }

    const src = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      setImage(img);
      setImageName(file.name);
      setError('');
      URL.revokeObjectURL(src);
    };

    img.onerror = () => {
      setError('Unable to read this image file.');
      URL.revokeObjectURL(src);
    };

    img.src = src;
  };

  const parseEditorImagePoint = (event) => {
    if (!image || !editorCanvasRef.current) return null;

    const canvas = editorCanvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    const canvasX = ((event.clientX - canvasRect.left) * canvas.width) / canvasRect.width;
    const canvasY = ((event.clientY - canvasRect.top) * canvas.height) / canvasRect.height;

    const drawMeta = drawMetaRef.current;
    if (
      canvasX < drawMeta.x ||
      canvasY < drawMeta.y ||
      canvasX > drawMeta.x + drawMeta.w ||
      canvasY > drawMeta.y + drawMeta.h
    ) {
      return null;
    }

    return {
      x: ((canvasX - drawMeta.x) / drawMeta.w) * image.width,
      y: ((canvasY - drawMeta.y) / drawMeta.h) * image.height,
    };
  };

  const onEditorMouseDown = (event) => {
    if (!image || !selectedPart) return;
    const point = parseEditorImagePoint(event);
    if (!point) return;

    drawingRef.current = {
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
    };
  };

  const onEditorMouseMove = (event) => {
    if (!drawingRef.current || !image) return;
    const point = parseEditorImagePoint(event);
    if (!point) return;

    drawingRef.current = {
      ...drawingRef.current,
      endX: point.x,
      endY: point.y,
    };

    drawRigEditor();
  };

  const onEditorMouseUp = (event) => {
    if (!drawingRef.current || !image || !selectedPart) return;

    const point = parseEditorImagePoint(event);
    if (point) {
      drawingRef.current = {
        ...drawingRef.current,
        endX: point.x,
        endY: point.y,
      };
    }

    const normalized = normalizeRect(
      drawingRef.current.startX,
      drawingRef.current.startY,
      drawingRef.current.endX,
      drawingRef.current.endY,
      image.width,
      image.height
    );

    drawingRef.current = null;

    if (normalized.w < 2 || normalized.h < 2) return;

    updatePart(selectedPart.id, { rect: normalized });
  };

  const drawRigEditor = () => {
    const canvas = editorCanvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, canvas.width, canvas.height);
    drawChecker(context, canvas.width, canvas.height, 16);

    if (!image) {
      context.fillStyle = '#dbeafe';
      context.font = '600 22px sans-serif';
      context.textAlign = 'center';
      context.fillText('Upload a source image to begin rigging', canvas.width / 2, canvas.height / 2);
      return;
    }

    const margin = 16;
    const availableWidth = canvas.width - margin * 2;
    const availableHeight = canvas.height - margin * 2;
    const scale = Math.min(availableWidth / image.width, availableHeight / image.height);
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;
    const drawX = (canvas.width - drawWidth) / 2;
    const drawY = (canvas.height - drawHeight) / 2;

    drawMetaRef.current = { x: drawX, y: drawY, w: drawWidth, h: drawHeight };

    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    const toCanvasPoint = (x, y) => ({
      x: drawX + (x / image.width) * drawWidth,
      y: drawY + (y / image.height) * drawHeight,
    });

    const byId = new Map(parts.map((part) => [part.id, part]));

    parts.forEach((part) => {
      if (!part.rect) return;

      const x = drawX + (part.rect.x / image.width) * drawWidth;
      const y = drawY + (part.rect.y / image.height) * drawHeight;
      const w = (part.rect.w / image.width) * drawWidth;
      const h = (part.rect.h / image.height) * drawHeight;

      context.strokeStyle = part.id === selectedPartId ? '#22d3ee' : '#93c5fd';
      context.lineWidth = part.id === selectedPartId ? 3 : 1.5;
      context.strokeRect(x, y, w, h);

      context.fillStyle = 'rgba(2, 132, 199, 0.2)';
      context.fillRect(x, y, w, h);

      const pivot = toCanvasPoint(
        part.rect.x + part.rect.w * part.pivotX,
        part.rect.y + part.rect.h * part.pivotY
      );

      context.fillStyle = '#facc15';
      context.beginPath();
      context.arc(pivot.x, pivot.y, 3.5, 0, Math.PI * 2);
      context.fill();

      if (part.parentId) {
        const parent = byId.get(part.parentId);
        if (parent?.rect) {
          const attach = toCanvasPoint(
            parent.rect.x + parent.rect.w * part.attachX,
            parent.rect.y + parent.rect.h * part.attachY
          );

          context.strokeStyle = '#34d399';
          context.lineWidth = 1.5;
          context.beginPath();
          context.moveTo(attach.x, attach.y);
          context.lineTo(pivot.x, pivot.y);
          context.stroke();

          context.fillStyle = '#34d399';
          context.beginPath();
          context.arc(attach.x, attach.y, 2.6, 0, Math.PI * 2);
          context.fill();
        }
      }

      context.fillStyle = '#e2e8f0';
      context.font = '12px sans-serif';
      context.fillText(part.name, x + 4, y + 14);
    });

    if (drawingRef.current) {
      const rect = normalizeRect(
        drawingRef.current.startX,
        drawingRef.current.startY,
        drawingRef.current.endX,
        drawingRef.current.endY,
        image.width,
        image.height
      );

      const x = drawX + (rect.x / image.width) * drawWidth;
      const y = drawY + (rect.y / image.height) * drawHeight;
      const w = (rect.w / image.width) * drawWidth;
      const h = (rect.h / image.height) * drawHeight;

      context.strokeStyle = '#f97316';
      context.lineWidth = 2;
      context.setLineDash([6, 4]);
      context.strokeRect(x, y, w, h);
      context.setLineDash([]);
    }
  };

  const computeFrameStates = (frameIndex, targetWidth, targetHeight) => {
    if (!image) return { states: new Map(), sortedParts: [] };

    const framePose = poseFrames[frameIndex] || {};
    const byId = new Map(parts.map((part) => [part.id, part]));
    const drawableParts = parts.filter((part) => part.rect && part.rect.w > 0 && part.rect.h > 0);

    if (drawableParts.length === 0) return { states: new Map(), sortedParts: [] };

    const roots = drawableParts.filter((part) => !part.parentId || !byId.has(part.parentId));
    const primaryRoot = drawableParts.find((part) => part.id === 'torso') || roots[0] || drawableParts[0];
    const rootPivotSource = {
      x: primaryRoot.rect.x + primaryRoot.rect.w * primaryRoot.pivotX,
      y: primaryRoot.rect.y + primaryRoot.rect.h * primaryRoot.pivotY,
    };

    const rootBase = {
      x: targetWidth * 0.5,
      y: targetHeight * 0.72,
    };

    const stateCache = new Map();

    const resolveState = (part) => {
      if (stateCache.has(part.id)) return stateCache.get(part.id);

      const pose = framePose[part.id] || { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
      const localRotation = degToRad(pose.rotation);
      const sourcePivot = {
        x: part.rect.x + part.rect.w * part.pivotX,
        y: part.rect.y + part.rect.h * part.pivotY,
      };

      const parent = part.parentId ? byId.get(part.parentId) : null;
      let state;

      if (parent?.rect) {
        const parentState = resolveState(parent);
        const parentAttachLocal = {
          x: (part.attachX - parent.pivotX) * parent.rect.w * outputScale,
          y: (part.attachY - parent.pivotY) * parent.rect.h * outputScale,
        };

        const parentAttachDelta = rotate(parentAttachLocal, parentState.rotation);
        const parentAttachGlobal = {
          x: parentState.pivot.x + parentAttachDelta.x,
          y: parentState.pivot.y + parentAttachDelta.y,
        };

        const parentAttachSource = {
          x: parent.rect.x + parent.rect.w * part.attachX,
          y: parent.rect.y + parent.rect.h * part.attachY,
        };

        const restVector = {
          x: (sourcePivot.x - parentAttachSource.x) * outputScale,
          y: (sourcePivot.y - parentAttachSource.y) * outputScale,
        };

        const restDelta = rotate(restVector, parentState.rotation);

        state = {
          pivot: {
            x: parentAttachGlobal.x + restDelta.x + (pose.offsetX || 0),
            y: parentAttachGlobal.y + restDelta.y + (pose.offsetY || 0),
          },
          rotation: parentState.rotation + localRotation,
        };
      } else {
        const rootRest = {
          x: (sourcePivot.x - rootPivotSource.x) * outputScale,
          y: (sourcePivot.y - rootPivotSource.y) * outputScale,
        };

        state = {
          pivot: {
            x: rootBase.x + rootRest.x + (pose.offsetX || 0),
            y: rootBase.y + rootRest.y + (pose.offsetY || 0),
          },
          rotation: localRotation,
        };
      }

      stateCache.set(part.id, state);
      return state;
    };

    const sortedParts = [...drawableParts].sort((a, b) => {
      const aPose = framePose[a.id] || {};
      const bPose = framePose[b.id] || {};
      const aZ = a.z + (Number(aPose.zOffset) || 0);
      const bZ = b.z + (Number(bPose.zOffset) || 0);
      return aZ - bZ;
    });
    sortedParts.forEach((part) => resolveState(part));

    return { states: stateCache, sortedParts };
  };

  const drawFrameToContext = (context, frameIndex, targetX, targetY, targetWidth, targetHeight, withBones) => {
    if (!image) return;

    const { states, sortedParts } = computeFrameStates(frameIndex, targetWidth, targetHeight);
    const framePose = poseFrames[frameIndex] || {};
    if (sortedParts.length === 0) return;

    const drawRigidPart = (part, state) => {
      const drawWidth = part.rect.w * outputScale;
      const drawHeight = part.rect.h * outputScale;
      const pivotX = drawWidth * part.pivotX;
      const pivotY = drawHeight * part.pivotY;

      context.save();
      context.translate(targetX + state.pivot.x, targetY + state.pivot.y);
      context.rotate(state.rotation);
      context.drawImage(
        image,
        part.rect.x,
        part.rect.y,
        part.rect.w,
        part.rect.h,
        -pivotX,
        -pivotY,
        drawWidth,
        drawHeight
      );
      context.restore();
    };

    const drawMeshPart = (part, state) => {
      const pose = framePose[part.id] || { offsetX: 0, offsetY: 0, rotation: 0, meshBend: 0, zOffset: 0 };
      const drawWidth = part.rect.w * outputScale;
      const drawHeight = part.rect.h * outputScale;
      const pivotX = drawWidth * part.pivotX;
      const pivotY = drawHeight * part.pivotY;

      const segments = clamp(meshSegments, 2, 24);
      const srcTall = part.rect.h >= part.rect.w;
      const axisLength = srcTall ? drawHeight : drawWidth;
      const segmentSize = axisLength / segments;
      const totalBendRad = degToRad(clamp(pose.meshBend || 0, -120, 120));

      context.save();
      context.translate(targetX + state.pivot.x, targetY + state.pivot.y);
      context.rotate(state.rotation);

      for (let i = 0; i < segments; i += 1) {
        const tCenter = (i + 0.5) / segments;
        const tNorm = tCenter - 0.5;
        const bendAtSlice = totalBendRad * tNorm;

        context.save();

        if (srcTall) {
          const localY = -pivotY + i * segmentSize;
          context.translate(0, localY + segmentSize * 0.5);
          context.rotate(bendAtSlice);
          context.drawImage(
            image,
            part.rect.x,
            part.rect.y + (part.rect.h / segments) * i,
            part.rect.w,
            Math.max(1, part.rect.h / segments),
            -pivotX,
            -segmentSize * 0.5,
            drawWidth,
            segmentSize + 0.8
          );
        } else {
          const localX = -pivotX + i * segmentSize;
          context.translate(localX + segmentSize * 0.5, 0);
          context.rotate(bendAtSlice);
          context.drawImage(
            image,
            part.rect.x + (part.rect.w / segments) * i,
            part.rect.y,
            Math.max(1, part.rect.w / segments),
            part.rect.h,
            -segmentSize * 0.5,
            -pivotY,
            segmentSize + 0.8,
            drawHeight
          );
        }

        context.restore();
      }

      context.restore();
    };

    sortedParts.forEach((part) => {
      const state = states.get(part.id);
      if (!state) return;
      if (meshEnabled) {
        drawMeshPart(part, state);
      } else {
        drawRigidPart(part, state);
      }
    });

    if (withBones) {
      const byId = new Map(parts.map((part) => [part.id, part]));

      sortedParts.forEach((part) => {
        const state = states.get(part.id);
        if (!state) return;

        if (part.parentId) {
          const parent = byId.get(part.parentId);
          const parentState = parent ? states.get(parent.id) : null;
          if (parent && parentState && parent.rect) {
            const parentAttachLocal = {
              x: (part.attachX - parent.pivotX) * parent.rect.w * outputScale,
              y: (part.attachY - parent.pivotY) * parent.rect.h * outputScale,
            };
            const attachDelta = rotate(parentAttachLocal, parentState.rotation);
            const attach = {
              x: targetX + parentState.pivot.x + attachDelta.x,
              y: targetY + parentState.pivot.y + attachDelta.y,
            };

            context.strokeStyle = '#34d399';
            context.lineWidth = 1.2;
            context.beginPath();
            context.moveTo(attach.x, attach.y);
            context.lineTo(targetX + state.pivot.x, targetY + state.pivot.y);
            context.stroke();
          }
        }

        context.fillStyle = part.id === selectedPartId ? '#facc15' : '#93c5fd';
        context.beginPath();
        context.arc(targetX + state.pivot.x, targetY + state.pivot.y, 2.8, 0, Math.PI * 2);
        context.fill();

        if (part.id === selectedPartId) {
          context.strokeStyle = '#f59e0b';
          context.lineWidth = 1.4;
          context.beginPath();
          context.arc(targetX + state.pivot.x, targetY + state.pivot.y, 14, 0, Math.PI * 2);
          context.stroke();
        }
      });
    }
  };

  const drawPoseEditor = () => {
    const canvas = poseCanvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = clamp(frameWidth, 16, 512);
    canvas.height = clamp(frameHeight, 16, 512);

    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, canvas.width, canvas.height);
    drawChecker(context, canvas.width, canvas.height, 8);

    if (!image) return;

    drawFrameToContext(context, activeFrame, 0, 0, canvas.width, canvas.height, true);
  };

  const drawSpritesheet = () => {
    const stripCanvas = stripCanvasRef.current;
    if (!stripCanvas) return;

    const context = stripCanvas.getContext('2d');
    if (!context) return;

    const w = clamp(frameWidth, 16, 512);
    const h = clamp(frameHeight, 16, 512);
    const count = clamp(frameCount, 1, 32);

    stripCanvas.width = w * count;
    stripCanvas.height = h;

    context.clearRect(0, 0, stripCanvas.width, stripCanvas.height);
    context.imageSmoothingEnabled = false;

    if (!image) return;

    for (let i = 0; i < count; i += 1) {
      drawFrameToContext(context, i, i * w, 0, w, h, false);
    }
  };

  const drawPreviewFrame = () => {
    const previewCanvas = previewCanvasRef.current;
    const stripCanvas = stripCanvasRef.current;
    if (!previewCanvas || !stripCanvas) return;

    const context = previewCanvas.getContext('2d');
    if (!context) return;

    const w = clamp(frameWidth, 16, 512);
    const h = clamp(frameHeight, 16, 512);

    previewCanvas.width = w;
    previewCanvas.height = h;

    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, w, h);
    drawChecker(context, w, h, 8);

    if (!image) return;

    const frame = clamp(previewFrame, 0, Math.max(0, frameCount - 1));
    context.drawImage(stripCanvas, frame * w, 0, w, h, 0, 0, w, h);
  };

  const getPoseCanvasPoint = (event) => {
    const canvas = poseCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
      width: canvas.width,
      height: canvas.height,
    };
  };

  const findPosePartAtPoint = (x, y, states, sortedParts) => {
    const candidates = [...sortedParts].sort((a, b) => b.z - a.z);

    for (const part of candidates) {
      const state = states.get(part.id);
      if (!state || !part.rect) continue;

      const drawWidth = part.rect.w * outputScale;
      const drawHeight = part.rect.h * outputScale;
      const pivotX = drawWidth * part.pivotX;
      const pivotY = drawHeight * part.pivotY;

      const dx = x - state.pivot.x;
      const dy = y - state.pivot.y;
      const inv = -state.rotation;
      const local = {
        x: dx * Math.cos(inv) - dy * Math.sin(inv) + pivotX,
        y: dx * Math.sin(inv) + dy * Math.cos(inv) + pivotY,
      };

      if (local.x >= 0 && local.y >= 0 && local.x <= drawWidth && local.y <= drawHeight) {
        return part;
      }

      const pivotDistance = Math.hypot(dx, dy);
      if (pivotDistance <= 8) {
        return part;
      }
    }

    return null;
  };

  const onPoseMouseDown = (event) => {
    if (!image || !poseCanvasRef.current) return;

    const point = getPoseCanvasPoint(event);
    if (!point) return;

    const { states, sortedParts } = computeFrameStates(activeFrame, point.width, point.height);
    const hitPart = findPosePartAtPoint(point.x, point.y, states, sortedParts);
    if (!hitPart) return;

    setSelectedPartId(hitPart.id);

    const pose = (poseFrames[activeFrame] || {})[hitPart.id] || { offsetX: 0, offsetY: 0, rotation: 0 };

    const hitState = states.get(hitPart.id);
    if (!hitState) return;

    const distanceToPivot = Math.hypot(point.x - hitState.pivot.x, point.y - hitState.pivot.y);
    const rotateRingInner = 10;
    const rotateRingOuter = 20;

    dragPoseRef.current = {
      partId: hitPart.id,
      startX: point.x,
      startY: point.y,
      startOffsetX: pose.offsetX,
      startOffsetY: pose.offsetY,
      startRotation: pose.rotation,
      startAngle: Math.atan2(point.y - hitState.pivot.y, point.x - hitState.pivot.x),
      pivotX: hitState.pivot.x,
      pivotY: hitState.pivot.y,
      mode: distanceToPivot >= rotateRingInner && distanceToPivot <= rotateRingOuter ? 'rotate' : 'move',
    };
  };

  const onPoseMouseMove = (event) => {
    if (!dragPoseRef.current) return;

    const point = getPoseCanvasPoint(event);
    if (!point) return;

    const x = point.x;
    const y = point.y;

    if (dragPoseRef.current.mode === 'rotate') {
      const currentAngle = Math.atan2(y - dragPoseRef.current.pivotY, x - dragPoseRef.current.pivotX);
      const deltaAngle = currentAngle - dragPoseRef.current.startAngle;
      const nextRotation = dragPoseRef.current.startRotation + (deltaAngle * 180) / Math.PI;

      updatePose(activeFrame, dragPoseRef.current.partId, {
        rotation: Number(nextRotation.toFixed(2)),
      });
      return;
    }

    const deltaX = x - dragPoseRef.current.startX;
    const deltaY = y - dragPoseRef.current.startY;

    updatePose(activeFrame, dragPoseRef.current.partId, {
      offsetX: Number((dragPoseRef.current.startOffsetX + deltaX).toFixed(2)),
      offsetY: Number((dragPoseRef.current.startOffsetY + deltaY).toFixed(2)),
    });
  };

  const onPoseMouseUp = () => {
    dragPoseRef.current = null;
  };

  const autoFitScale = () => {
    const rectParts = parts.filter((part) => part.rect);
    if (rectParts.length === 0) return;

    const minX = Math.min(...rectParts.map((part) => part.rect.x));
    const minY = Math.min(...rectParts.map((part) => part.rect.y));
    const maxX = Math.max(...rectParts.map((part) => part.rect.x + part.rect.w));
    const maxY = Math.max(...rectParts.map((part) => part.rect.y + part.rect.h));

    const rigW = maxX - minX;
    const rigH = maxY - minY;

    if (rigW <= 0 || rigH <= 0) return;

    const fitScale = Math.min((frameWidth * 0.68) / rigW, (frameHeight * 0.8) / rigH);
    setOutputScale(clamp(Number(fitScale.toFixed(2)), 0.1, 8));
  };

  const downloadSpritesheet = () => {
    const canvas = stripCanvasRef.current;
    if (!canvas || !image) return;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const baseName = imageName.includes('.') ? imageName.slice(0, imageName.lastIndexOf('.')) : 'sprite';
      link.href = url;
      link.download = `${baseName}-thembones-${frameWidth}x${frameHeight}-${frameCount}f.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  useEffect(() => {
    drawRigEditor();
  }, [image, parts, selectedPartId]);

  useEffect(() => {
    drawPoseEditor();
    drawSpritesheet();
    drawPreviewFrame();
  }, [
    image,
    parts,
    poseFrames,
    activeFrame,
    previewFrame,
    frameCount,
    frameWidth,
    frameHeight,
    outputScale,
    selectedPartId,
  ]);

  useEffect(() => {
    if (!playPreview || frameCount <= 1) return undefined;

    const frameDuration = Math.round(1000 / clamp(fps, 1, 30));
    const id = window.setInterval(() => {
      setPreviewFrame((current) => (current + 1) % frameCount);
    }, frameDuration);

    return () => window.clearInterval(id);
  }, [playPreview, fps, frameCount]);

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1>ThemBones Pixel Rigging Lab</h1>
        <p>
          Draw body-part rectangles, set bone parent links, and then hand-pose each frame.
          Drag the selected part in the pose canvas and tune rotation for full animation control.
        </p>

        <div className={styles.topControls}>
          <label className={styles.uploadField}>
            <span>Source Image</span>
            <input type="file" accept="image/*" onChange={onImageUpload} />
          </label>

          <button type="button" onClick={resetRig} className={styles.secondaryButton}>
            Reset Default Rig
          </button>

          <button type="button" onClick={addPart} className={styles.secondaryButton}>
            Add Bone Part
          </button>

          <button
            type="button"
            onClick={removeSelectedPart}
            className={styles.secondaryButton}
            disabled={!selectedPart || parts.length <= 1}
          >
            Remove Selected
          </button>
        </div>

        <div className={styles.layout}>
          <section className={styles.editorPanel}>
            <h2>1) Rig Editor</h2>
            <p>
              Select a part, then drag on the source image to create its bounding rectangle.
              Yellow dots are pivots and green links show parent-child bone relationships.
            </p>
            <canvas
              ref={editorCanvasRef}
              className={styles.editorCanvas}
              width={960}
              height={540}
              onMouseDown={onEditorMouseDown}
              onMouseMove={onEditorMouseMove}
              onMouseUp={onEditorMouseUp}
              onMouseLeave={onEditorMouseUp}
            />
          </section>

          <aside className={styles.controlPanel}>
            <h2>2) Bone Setup</h2>

            <label className={styles.field}>
              <span>Part</span>
              <select
                value={selectedPartId}
                onChange={(event) => setSelectedPartId(event.target.value)}
              >
                {parts.map((part) => (
                  <option key={part.id} value={part.id}>
                    {part.name}
                  </option>
                ))}
              </select>
            </label>

            {selectedPart && (
              <>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    value={selectedPart.name}
                    onChange={(event) => updatePart(selectedPart.id, { name: event.target.value })}
                  />
                </label>

                <label className={styles.field}>
                  <span>Parent</span>
                  <select
                    value={selectedPart.parentId}
                    onChange={(event) => updatePart(selectedPart.id, { parentId: event.target.value })}
                  >
                    <option value="">None (root)</option>
                    {parts
                      .filter((part) => part.id !== selectedPart.id)
                      .map((part) => (
                        <option key={part.id} value={part.id}>
                          {part.name}
                        </option>
                      ))}
                  </select>
                </label>

                <label className={styles.field}>
                  <span>Pivot X: {selectedPart.pivotX.toFixed(2)}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={selectedPart.pivotX}
                    onChange={(event) =>
                      updatePart(selectedPart.id, {
                        pivotX: clamp(Number(event.target.value), 0, 1),
                      })
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Pivot Y: {selectedPart.pivotY.toFixed(2)}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={selectedPart.pivotY}
                    onChange={(event) =>
                      updatePart(selectedPart.id, {
                        pivotY: clamp(Number(event.target.value), 0, 1),
                      })
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Attach X on parent: {selectedPart.attachX.toFixed(2)}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={selectedPart.attachX}
                    onChange={(event) =>
                      updatePart(selectedPart.id, {
                        attachX: clamp(Number(event.target.value), 0, 1),
                      })
                    }
                    disabled={!selectedPart.parentId}
                  />
                </label>

                <label className={styles.field}>
                  <span>Attach Y on parent: {selectedPart.attachY.toFixed(2)}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={selectedPart.attachY}
                    onChange={(event) =>
                      updatePart(selectedPart.id, {
                        attachY: clamp(Number(event.target.value), 0, 1),
                      })
                    }
                    disabled={!selectedPart.parentId}
                  />
                </label>

                <label className={styles.field}>
                  <span>Draw Order (z): {selectedPart.z}</span>
                  <input
                    type="range"
                    min="0"
                    max="40"
                    step="1"
                    value={selectedPart.z}
                    onChange={(event) => updatePart(selectedPart.id, { z: Number(event.target.value) })}
                  />
                </label>

                <p className={styles.metaText}>
                  {selectedPart.rect
                    ? `Rect: ${selectedPart.rect.x}, ${selectedPart.rect.y}, ${selectedPart.rect.w}x${selectedPart.rect.h}`
                    : 'No rectangle yet. Drag on the source image to assign one.'}
                </p>
              </>
            )}
          </aside>
        </div>

        <section className={styles.outputSection}>
          <h2>3) Frame Posing + Spritesheet</h2>

          <div className={styles.outputControls}>
            <label className={styles.field}>
              <span>Frames</span>
              <input
                type="number"
                min="1"
                max="32"
                value={frameCount}
                onChange={(event) => setFrameCount(clamp(Number(event.target.value) || 1, 1, 32))}
              />
            </label>

            <label className={styles.field}>
              <span>Frame Width</span>
              <input
                type="number"
                min="16"
                max="512"
                value={frameWidth}
                onChange={(event) => setFrameWidth(clamp(Number(event.target.value) || 16, 16, 512))}
              />
            </label>

            <label className={styles.field}>
              <span>Frame Height</span>
              <input
                type="number"
                min="16"
                max="512"
                value={frameHeight}
                onChange={(event) => setFrameHeight(clamp(Number(event.target.value) || 16, 16, 512))}
              />
            </label>

            <label className={styles.field}>
              <span>Character Scale: {outputScale.toFixed(2)}x</span>
              <input
                type="range"
                min="0.1"
                max="8"
                step="0.05"
                value={outputScale}
                onChange={(event) => setOutputScale(clamp(Number(event.target.value), 0.1, 8))}
              />
            </label>

            <label className={styles.field}>
              <span>Active Frame: {activeFrame + 1}</span>
              <input
                type="range"
                min="0"
                max={Math.max(0, frameCount - 1)}
                step="1"
                value={clamp(activeFrame, 0, Math.max(0, frameCount - 1))}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setActiveFrame(next);
                  setPreviewFrame(next);
                }}
              />
            </label>

            <label className={styles.field}>
                <span>Selected Rotation: {selectedPose.rotation.toFixed(1)}°</span>
              <input
                type="range"
                min="-180"
                max="180"
                step="0.5"
                value={selectedPose.rotation}
                onChange={(event) =>
                  selectedPart &&
                  updatePose(activeFrame, selectedPart.id, {
                    rotation: Number(event.target.value),
                  })
                }
                disabled={!selectedPart}
              />
            </label>

            <label className={styles.field}>
              <span>Selected Mesh Bend: {selectedPose.meshBend.toFixed(1)}°</span>
              <input
                type="range"
                min="-90"
                max="90"
                step="0.5"
                value={selectedPose.meshBend}
                onChange={(event) =>
                  selectedPart &&
                  updatePose(activeFrame, selectedPart.id, {
                    meshBend: Number(event.target.value),
                  })
                }
                disabled={!selectedPart}
              />
            </label>

            <label className={styles.field}>
              <span>Selected Draw Order Offset: {selectedPose.zOffset.toFixed(0)}</span>
              <input
                type="range"
                min="-40"
                max="40"
                step="1"
                value={selectedPose.zOffset}
                onChange={(event) =>
                  selectedPart &&
                  updatePose(activeFrame, selectedPart.id, {
                    zOffset: Number(event.target.value),
                  })
                }
                disabled={!selectedPart}
              />
            </label>

            <label className={styles.field}>
              <span>Mesh Deformation</span>
              <select
                value={meshEnabled ? 'on' : 'off'}
                onChange={(event) => setMeshEnabled(event.target.value === 'on')}
              >
                <option value="on">Enabled</option>
                <option value="off">Disabled</option>
              </select>
            </label>

            <label className={styles.field}>
              <span>Mesh Segments: {meshSegments}</span>
              <input
                type="range"
                min="2"
                max="24"
                step="1"
                value={meshSegments}
                onChange={(event) => setMeshSegments(clamp(Number(event.target.value) || 2, 2, 24))}
                disabled={!meshEnabled}
              />
            </label>

            <label className={styles.field}>
              <span>Preview FPS</span>
              <input
                type="number"
                min="1"
                max="30"
                value={fps}
                onChange={(event) => setFps(clamp(Number(event.target.value) || 1, 1, 30))}
              />
            </label>
          </div>

          <div className={styles.actionRow}>
            <button type="button" className={styles.secondaryButton} onClick={copyFromPreviousFrame}>
              Copy Prev Frame Pose
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => clearFramePose(activeFrame)}>
              Clear Active Frame Pose
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                if (!selectedPart) return;
                openConfirm(`Reset pose for ${selectedPart.name} in frame ${activeFrame + 1}?`, () => {
                  updatePose(activeFrame, selectedPart.id, {
                    offsetX: 0,
                    offsetY: 0,
                    rotation: 0,
                    meshBend: 0,
                    zOffset: 0,
                  });
                });
              }}
              disabled={!selectedPart}
            >
              Reset Selected Bone Pose
            </button>
            <button type="button" className={styles.secondaryButton} onClick={autoFitScale}>
              Auto-Fit Scale
            </button>
            <button type="button" className={styles.primaryButton} onClick={downloadSpritesheet} disabled={!image}>
              Download PNG Spritesheet
            </button>
          </div>

          <div className={styles.previewRow}>
            <div className={styles.previewPane}>
              <h3>Pose Editor</h3>
              <p className={styles.metaText}>Click part to select. Drag part to move. Drag the orange ring around selected pivot to rotate.</p>
              <canvas
                ref={poseCanvasRef}
                className={styles.previewCanvas}
                onMouseDown={onPoseMouseDown}
                onMouseMove={onPoseMouseMove}
                onMouseUp={onPoseMouseUp}
                onMouseLeave={onPoseMouseUp}
              />
            </div>

            <div className={styles.previewPane}>
              <h3>Animation Preview</h3>
              <canvas ref={previewCanvasRef} className={styles.previewCanvas} />
              <label className={styles.field}>
                <span>Preview Frame: {previewFrame + 1}</span>
                <input
                  type="range"
                  min="0"
                  max={Math.max(0, frameCount - 1)}
                  step="1"
                  value={clamp(previewFrame, 0, Math.max(0, frameCount - 1))}
                  onChange={(event) => setPreviewFrame(Number(event.target.value))}
                />
              </label>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setPlayPreview((value) => !value)}
              >
                {playPreview ? 'Pause Preview' : 'Play Preview'}
              </button>
            </div>
          </div>

          <div className={styles.stripPane}>
            <h3>Generated Spritesheet</h3>
            <canvas ref={stripCanvasRef} className={styles.stripCanvas} />
          </div>

          {error && <p className={styles.error}>{error}</p>}
        </section>

        {confirmState.open && (
          <div className={styles.modalOverlay} role="presentation" onClick={closeConfirm}>
            <div
              className={styles.modalCard}
              role="dialog"
              aria-modal="true"
              aria-label="Confirm action"
              onClick={(event) => event.stopPropagation()}
            >
              <h3>Confirm Action</h3>
              <p>{confirmState.message}</p>
              <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryButton} onClick={closeConfirm}>
                  Cancel
                </button>
                <button type="button" className={styles.primaryButton} onClick={acceptConfirm}>
                  Yes, Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default ThemBones;
